import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  HeartPulse, Plus, RefreshCw, Trash2, Eye, CheckCircle2, Clock,
  Loader2, Search, X, Users as UsersIcon, FileUp,
} from "lucide-react";

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); }
  catch { return iso; }
};

// Human-readable summary of the employee choice for the table cell.
const choiceLabel = (c) => {
  if (!c) return null;
  if (c.interesse === "nao_tem") return "NÃO ADERIU";
  if (c.interesse === "tem") {
    const n = Array.isArray(c.dependentes) ? c.dependentes.length : 0;
    if (c.tem_dependentes && n > 0) {
      return `ADERIU · ${n} depend${n === 1 ? "ente" : "entes"}`;
    }
    return "ADERIU · sem dependentes";
  }
  return null;
};


function CreateNaoAdesaoDialog({ open, onOpenChange, onCreated, adminToken }) {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [variant, setVariant] = useState("com_dependentes");
  const [busy, setBusy] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // Debounce the search input so we don't spam the API.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch server-side using the debounced search term. The employees
  // endpoint is paginated (50/page cap), so client-side filtering is
  // never enough — we ALWAYS defer the match to the server.
  useEffect(() => {
    if (!open) return;
    setLoadingList(true);
    const params = { per_page: 100 };
    if (debounced) params.search = debounced;
    axios.get(`${API}/admin/employees`, {
      headers: { Authorization: `Bearer ${adminToken}` }, params,
    }).then((r) => {
      const list = Array.isArray(r.data) ? r.data
        : (r.data?.employees || []);
      setEmployees(list);
    }).catch(() => setEmployees([]))
      .finally(() => setLoadingList(false));
  }, [open, adminToken, debounced]);

  useEffect(() => {
    if (!open) {
      setEmployeeId(""); setSearch(""); setDebounced("");
      setVariant("com_dependentes");
    }
  }, [open]);

  const selected = employees.find((e) => e.id === employeeId);

  const submit = async () => {
    if (!employeeId) return toast.error("Selecione um funcionário");
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/nao-adesao/create`,
        { employee_id: employeeId, variant },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          timeout: 30000,
        },
      );
      toast.success("Declaração de Adesão criada");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[95vw]"
        data-testid="na-create-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-emerald-700" />
            Nova Declaração — Adesão Plano de Saúde
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-600">Funcionário</Label>
            <div className="relative mt-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por nome, matrícula ou CPF…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="na-emp-search"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              A busca é feita no servidor — a lista abaixo mostra apenas os
              primeiros 100 resultados. Digite para filtrar entre todos os
              funcionários.
            </p>
          </div>

          <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
            {loadingList ? (
              <div className="px-3 py-6 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
              </div>
            ) : employees.length === 0 ? (
              <div className="px-3 py-6 text-sm text-slate-500 text-center">
                {search ? "Sem resultados" : "Sem funcionários"}
              </div>
            ) : employees.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => setEmployeeId(e.id)}
                className={`w-full text-left px-3 py-2 hover:bg-emerald-50 transition ${
                  employeeId === e.id ? "bg-emerald-100" : ""
                }`}
                data-testid={`na-emp-opt-${e.id}`}
              >
                <div className="text-sm font-medium text-slate-900">{e.nome}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  Matrícula {e.matricula || "—"} · CPF {e.cpf || "—"}
                </div>
              </button>
            ))}
          </div>

          {selected && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <div className="flex items-center gap-2 text-emerald-800 font-medium">
                <UsersIcon className="w-4 h-4" />
                {selected.nome}
              </div>
              <div className="text-[11px] text-emerald-700 mt-0.5">
                Mat. {selected.matricula || "—"} · CPF {selected.cpf || "—"}
              </div>
            </div>
          )}

          {/* Model variant selector */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <label className="text-xs font-semibold text-slate-700 block mb-1.5">
              Modelo do documento
            </label>
            <div className="flex gap-2 flex-col sm:flex-row">
              <label
                className={`flex-1 border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                  variant === "com_dependentes"
                    ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-400"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid="na-variant-com"
              >
                <input
                  type="radio" name="na-variant" value="com_dependentes"
                  checked={variant === "com_dependentes"}
                  onChange={() => setVariant("com_dependentes")}
                  className="mr-1.5"
                />
                <span className="text-xs font-semibold">Com dependentes</span>
                <p className="text-[10px] text-slate-500 mt-0.5 ml-4">
                  Layout padrão · o funcionário pode listar até 6 dependentes.
                </p>
              </label>
              <label
                className={`flex-1 border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                  variant === "sem_dependentes"
                    ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-400"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid="na-variant-sem"
              >
                <input
                  type="radio" name="na-variant" value="sem_dependentes"
                  checked={variant === "sem_dependentes"}
                  onChange={() => setVariant("sem_dependentes")}
                  className="mr-1.5"
                />
                <span className="text-xs font-semibold">Sem dependentes</span>
                <p className="text-[10px] text-slate-500 mt-0.5 ml-4">
                  Modelo individual · a seção de dependentes é omitida.
                </p>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            <X className="w-4 h-4 mr-2" />Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !employeeId}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="na-create-submit"
          >
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Criando…</>
              : <><Plus className="w-4 h-4 mr-2" />Criar declaração</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------------------- Bulk upload dialog --------------------
// Admin uploads a CSV/XLSX/text list with matricula, nome, cpf and the
// backend creates Não Adesão declarations for every employee it can
// match. Any missing or ambiguous rows come back so the admin can fix
// the source file and retry.
function BulkNaoAdesaoDialog({ open, onOpenChange, onDone, adminToken }) {
  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [variant, setVariant] = useState("com_dependentes");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!open) {
      setFile(null); setPasteText(""); setReport(null); setBusy(false);
      setVariant("com_dependentes");
    }
  }, [open]);

  const submit = async () => {
    if (!file && !pasteText.trim()) {
      return toast.error("Selecione um arquivo ou cole a lista");
    }
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      if (pasteText.trim()) fd.append("text", pasteText.trim());
      fd.append("variant", variant);
      const r = await axios.post(
        `${API}/admin/nao-adesao/bulk-create`, fd,
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        },
      );
      setReport(r.data);
      const c = r.data?.created?.length || 0;
      if (c > 0) toast.success(`${c} declaração(ões) criada(s)`);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload em massa");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[95vw]"
        data-testid="na-bulk-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="w-5 h-5 text-emerald-700" />
            Emitir em Lote — Adesão Plano de Saúde
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-xs text-slate-600 leading-relaxed">
            Envie um arquivo <strong>CSV, TXT ou XLSX</strong> com uma
            coluna por linha:{" "}
            <span className="font-mono text-[11px]">
              matrícula, nome, CPF
            </span>. Também pode colar a lista abaixo — separando os
            valores por vírgula, tabulação ou ponto-e-vírgula.
          </p>

          <div>
            <Label className="text-xs text-slate-600">
              Arquivo (opcional)
            </Label>
            <Input
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1"
              data-testid="na-bulk-file"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-600">
              …ou cole a lista aqui
            </Label>
            <textarea
              rows={6}
              placeholder={"01234, JOÃO DA SILVA, 000.000.000-00\n05678, MARIA APARECIDA, 111.222.333-44"}
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white p-2 text-xs font-mono resize-y"
              data-testid="na-bulk-paste"
            />
          </div>

          {report && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2 text-xs">
              <div className="font-bold text-slate-800">Resultado</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] text-emerald-700 font-semibold">CRIADAS</div>
                  <div className="text-xl font-bold text-emerald-800">
                    {report.created?.length || 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-amber-700 font-semibold">REUTILIZADAS</div>
                  <div className="text-xl font-bold text-amber-800">
                    {report.reused?.length || 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-red-700 font-semibold">NÃO ENCONTRADOS</div>
                  <div className="text-xl font-bold text-red-800">
                    {report.missing?.length || 0}
                  </div>
                </div>
              </div>
              {report.missing?.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold text-red-700 mt-2">
                    Linhas não localizadas (verifique matrícula/CPF):
                  </div>
                  <ul className="mt-1 max-h-32 overflow-y-auto rounded border border-red-200 bg-white p-2 space-y-0.5">
                    {report.missing.map((m, i) => (
                      <li key={i} className="font-mono text-[10px] text-red-800">
                        {typeof m === "string" ? m : (
                          `linha ${m.line || i + 1}: ${m.raw || JSON.stringify(m)}${
                            m.reason ? ` — ${m.reason}` : ""
                          }`
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Model variant selector (applies to all rows in this batch) */}
          <div className="border rounded-lg p-3 bg-slate-50">
            <label className="text-xs font-semibold text-slate-700 block mb-1.5">
              Modelo do documento (para todos os funcionários do lote)
            </label>
            <div className="flex gap-2 flex-col sm:flex-row">
              <label
                className={`flex-1 border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                  variant === "com_dependentes"
                    ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-400"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid="na-bulk-variant-com"
              >
                <input
                  type="radio" name="na-bulk-variant" value="com_dependentes"
                  checked={variant === "com_dependentes"}
                  onChange={() => setVariant("com_dependentes")}
                  className="mr-1.5"
                />
                <span className="text-xs font-semibold">Com dependentes</span>
              </label>
              <label
                className={`flex-1 border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                  variant === "sem_dependentes"
                    ? "border-emerald-500 bg-emerald-50 ring-1 ring-emerald-400"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid="na-bulk-variant-sem"
              >
                <input
                  type="radio" name="na-bulk-variant" value="sem_dependentes"
                  checked={variant === "sem_dependentes"}
                  onChange={() => setVariant("sem_dependentes")}
                  className="mr-1.5"
                />
                <span className="text-xs font-semibold">Sem dependentes</span>
              </label>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            <X className="w-4 h-4 mr-2" />Fechar
          </Button>
          <Button
            onClick={submit}
            disabled={busy || (!file && !pasteText.trim())}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="na-bulk-submit"
          >
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processando…</>
              : <><FileUp className="w-4 h-4 mr-2" />Emitir em lote</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminNaoAdesaoTab({ adminToken }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewName, setPreviewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/nao-adesao`, {
        headers: { Authorization: `Bearer ${adminToken}` }, timeout: 15000,
      });
      setRows(r.data || []);
    } catch { toast.error("Falha ao carregar"); }
    finally { setLoading(false); }
  }, [adminToken]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (r.employee_nome || "").toLowerCase().includes(q)
        || (r.employee_matricula || "").toLowerCase().includes(q);
  });

  const openPreview = async (row) => {
    try {
      const r = await axios.get(
        `${API}/admin/nao-adesao/${row.id}/preview`,
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: "blob", timeout: 30000,
        },
      );
      setPreviewUrl(URL.createObjectURL(r.data));
      setPreviewName(
        `Adesão Plano de Saúde — ${row.employee_nome || row.id.slice(0, 8)}`,
      );
    } catch { toast.error("Não foi possível abrir o PDF"); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(""); setPreviewName("");
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Excluir declaração de Adesão de ${row.employee_nome}?`,
    )) return;
    try {
      await axios.delete(`${API}/admin/nao-adesao/${row.id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      toast.success("Excluída"); load();
    } catch { toast.error("Falha ao excluir"); }
  };

  const pending = rows.filter((r) => r.status !== "signed").length;
  const signed = rows.filter((r) => r.status === "signed").length;
  const aderiu = rows.filter(
    (r) => r.choice?.interesse === "tem",
  ).length;
  const naoAderiu = rows.filter(
    (r) => r.choice?.interesse === "nao_tem",
  ).length;

  return (
    <div className="space-y-4" data-testid="admin-na-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-emerald-700" />
            Adesão Plano de Saúde
          </h2>
          <p className="text-xs text-slate-500">
            Declaração de opção pelo plano de saúde empresarial +
            cadastro de dependentes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={load} disabled={loading}
            data-testid="na-refresh-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <RefreshCw className="w-4 h-4 mr-2" />}
            Atualizar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulkOpen(true)}
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            data-testid="na-bulk-btn"
          >
            <FileUp className="w-4 h-4 mr-2" />Emitir em lote
          </Button>
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="na-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />Nova Declaração
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[10px] text-slate-500 uppercase font-bold">
            Total
          </div>
          <div className="text-2xl font-bold text-slate-900">{rows.length}</div>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] text-amber-700 uppercase font-bold">
            Pendentes
          </div>
          <div className="text-2xl font-bold text-amber-900">{pending}</div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] text-emerald-700 uppercase font-bold">
            Assinadas
          </div>
          <div className="text-2xl font-bold text-emerald-900">{signed}</div>
        </div>
        <div className="rounded-md border border-teal-200 bg-teal-50 p-3">
          <div className="text-[10px] text-teal-700 uppercase font-bold">
            Aderiram
          </div>
          <div className="text-2xl font-bold text-teal-900">{aderiu}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <div className="text-[10px] text-slate-600 uppercase font-bold">
            Não aderiram
          </div>
          <div className="text-2xl font-bold text-slate-900">{naoAderiu}</div>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Filtrar por nome ou matrícula…"
          className="pl-9"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">Funcionário</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-center">Escolha</th>
              <th className="px-3 py-2 text-center">Criada</th>
              <th className="px-3 py-2 text-center">Assinada</th>
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-8 text-center text-slate-500"
                >
                  {rows.length === 0
                    ? 'Nenhuma. Clique "Nova Declaração" acima.'
                    : "Sem resultados."}
                </td>
              </tr>
            ) : filtered.map((r) => (
              <tr
                key={r.id}
                className="border-t border-slate-100"
                data-testid={`na-row-${r.id}`}
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-900">
                    {r.employee_nome || "—"}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono">
                    Mat. {r.employee_matricula || "—"}
                  </div>
                </td>
                <td className="px-3 py-2 text-center">
                  {r.status === "signed" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Assinada
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-amber-700 border-amber-300"
                    >
                      <Clock className="w-3 h-3 mr-1" />
                      Pendente
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  {choiceLabel(r.choice) ? (
                    <span
                      className={
                        r.choice?.interesse === "tem"
                          ? "text-teal-700 font-semibold"
                          : "text-slate-700 font-semibold"
                      }
                    >
                      {choiceLabel(r.choice)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  {fmtDateTime(r.created_at)}
                </td>
                <td className="px-3 py-2 text-center text-[10px]">
                  {r.signed_at ? fmtDateTime(r.signed_at) : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openPreview(r)}
                      data-testid={`na-preview-${r.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(r)}
                      data-testid={`na-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog
        open={!!previewUrl}
        onOpenChange={(v) => !v && closePreview()}
      >
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90vh] flex flex-col"
        >
          <DialogHeader><DialogTitle>{previewName}</DialogTitle></DialogHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title="Adesão Plano de Saúde preview"
              className="flex-1 w-full rounded-md border border-slate-200"
            />
          )}
        </DialogContent>
      </Dialog>

      <CreateNaoAdesaoDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
        adminToken={adminToken}
      />
      <BulkNaoAdesaoDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onDone={load}
        adminToken={adminToken}
      />
    </div>
  );
}
